import {
  isArray,
  isPlainObject,
  isString,
  cloneDeep,
} from 'lodash-es'

class LineItemUtils {

  /**
   * Checks to see if a payload is acceptable for the remaining
   * logic and throws error if not
   * @param  {Array} payload Array of objects
   * @return {none}         no return if payload is acceptable.
   */
  static checkPayload(payload) {
    if (!isArray(payload)) throw new Error('method `checkPayload` requires argument of type array')

    // There are situations where the `lineItems` payload may be empty
    // and instead of checking for length up front, when this maps a payload
    // to a 0 length array it results in nothing. Seems effective enough and
    // allows the code to be a little more terse.
  }

  /**
   * Applies a given payload to an array of objects
   * @param  {Array} lineItems Array of line item objects
   * @param  {Object} payload   Object to apply to each line item
   * @return {Array}           Transformed line items with the object applied to them
   */
  static applyPayloadToLineItems(lineItems, payload) {
    if (!isArray(lineItems)) throw new Error('method `applyPayloadToLineItems` requires `lineItems` argument of type array')
    if (!isPlainObject(payload)) throw new Error('method `applyPayloadToLineItems` requires `payload` argument of type object')

    let transformedLineItems = []

    // Check to see if there are any line-items (normal) before trying to apply transform.
    this.checkPayload(lineItems)
    transformedLineItems = lineItems.map((item) => {
      Object.assign(item, payload)
      return item
    })

    return transformedLineItems
  }

  /**
   * Applies a ship method to an array of line item objects
   * @param  {Array} lineItems The array of line item objects
   * @param  {String} method    The ship method as a string
   * @return {Array}           Transformed line items with the ship method applied to them
   */
  static applyShipMethodToLineItems(lineItems, method) {
    if (!isArray(lineItems)) throw new Error('method `applyShipMethodToLineItems` requires `lineItems` argument of type array')
    if (!isString(method)) throw new Error('method `applyShipMethodToLineItems` requires `method` argument of type string')

    const transformedLineItems = this.applyPayloadToLineItems(lineItems, {
      shipMethod: {
        id: method,
      },
    })

    return transformedLineItems
  }

  /**
   * Resets a given property on each line item
   * @param  {Array} lineItems The array of line item objects
   * @param  {String} property  Property to reset
   * @param  {Multiple} value     Any value to assign to the property
   * @return {Array}           Transformed line items with the property reset to value
   */
  static resetPropertyOnLineItems(lineItems, property, value) {
    if (!isArray(lineItems)) throw new Error('method `resetPropertyOnLineItems` requires `lineItems` argument of type array')
    if (!isString(property)) throw new Error('method `resetPropertyOnLineItems` requires `property` argument of type string')
    if (value === undefined) throw new Error('method `resetPropertyOnLineItems` requires `value` argument')

    let transformedLineItems = []

    // Check to see if there are any line-items (normal) before trying to apply transform.
    this.checkPayload(lineItems)
    transformedLineItems = lineItems.map((item) => {
      Object.assign(item, { [property]: value })
      return item
    })

    return transformedLineItems
  }

  /**
   * Resets the ship method on each line item
   * @param  {Array} lineItems The array of line item objects
   * @return {Array}           Transformed line items with the ship method property reset to null
   */
  static resetShipMethodOnLineItems(lineItems) {
    if (!isArray(lineItems)) throw new Error('method `resetShipMethodOnLineItems` requires `lineItems` argument of type array')

    const transformedLineItems = this.resetPropertyOnLineItems(lineItems, 'shipMethod', null)
    return transformedLineItems
  }

  /**
   * Resets the fulfillments property on each line item
   * @param  {Array} lineItems The array of line item objects
   * @return {Array}           Transformed line items with the fulfillments property reset to null
   */
  static resetFulfillmentsOnLineItems(lineItems) {
    if (!isArray(lineItems)) throw new Error('method `resetFulfillmentsOnLineItems` requires `lineItems` argument of type array')

    const transformedLineItems = this.resetPropertyOnLineItems(lineItems, 'fulfillments', null)
    return transformedLineItems
  }

  /**
   * Resets the shipToAddress property on each line item
   * @param  {Array} lineItems The array of line item objects
   * @return {Array}   Transformed line items with the shipToAddress property reset to null
   */
  static resetAddressIdOnLineItems(lineItems) {
    if (!isArray(lineItems)) throw new Error('method `resetAddressIdOnLineItems` requires `lineItems` argument of type array')

    const transformedLineItems = this.resetPropertyOnLineItems(lineItems, 'shippingAddress', null)
    return transformedLineItems
  }

  /**
   * Used to merge the model with a new dataset that is (likely) not
   * symmetrical or in the same order as the original. Because, when an
   * update is performed to the shipping informations for items, the data
   * represents a filtered subset of the data, there is a high likelihood
   * that the two objects will not be symmetrical and therefore the
   * normal use of merge will not suffice. This aims to create a little
   * more complex merging strategy so that properties are not accidentally
   * overwritten
   * @param  {Object} model   The original model (includes all lineItems)
   * @param  {Object} newData The new data. represents only ship items or pickup items
   * @return {Object}         The merged data.
   */
  static smartMerge(model, newData) {
    const copy = cloneDeep(model)
    newData.forEach((newDataItem) => {
      copy.forEach((modelItem) => {
        if (newDataItem.reiRefId === modelItem.reiRefId) {
          Object.assign(modelItem, newDataItem)
        }
      })
    })

    return copy
  }
}

export default LineItemUtils
